import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { Loading } from "go-core/components/Loading";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { organizationApi } from "../../../../../../../../../services/Api/Organization/organizationApi";
import {
	OrganizationUserApi,
	allPermissions,
} from "../../../../../../../../../services/Api/Organization/organizationTypes";
import { OrganizationApplicationApi } from "../../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../../services/organizations/selectors";
import OrganizationApplicationsTableForm from "../OrganizationApplicationsTableForm";

interface Props {
	organizationUserId: number | undefined;
	handleSave: (data: OrganizationUserApi) => void;
	handleClose: () => void;
}

const OrganizationUserForm = (props: Props) => {
	const { t } = useTranslation();
	const [organizationUser, setOrganizationUser] = useState<OrganizationUserApi | undefined>(undefined);
	const [applications, setApplications] = useState<OrganizationApplicationApi[]>([]);
	const form = useForm<OrganizationUserApi>({
		criteriaMode: "all",
		defaultValues: organizationUser ? organizationUser : {},
	});
	const organization = useSelector(selectOrganization);
	const permissions = allPermissions().map((per) => {
		return { value: per, label: t(`enums.users.permissions.${per}`) };
	});
	const {
		handleSubmit,
		formState: { errors },
		control,
		reset,
		register,
		setError,
	} = form;
	const { fields, remove, append } = useFieldArray({
		control,
		name: "applications",
		keyName: "key",
	});
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();

	useEffect(() => {
		init();
	}, []);

	const init = async () => {
		await fetchOrganization();
		await fetchApplications();
	};

	const getParsedDataForm = (data: OrganizationUserApi) => {
		if (organizationUser?.id) {
			data.id = organizationUser.id;
		}

		if (data.applications === undefined) data.applications = [];

		return data;
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		const newData = getParsedDataForm(data);
		try {
			const res = await organizationApi.updateOrganizationUser(data.id, newData);
			reset(data);
			addSuccessFlash(t("flash.users.user_updated"));
			props.handleSave(res);
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const fetchOrganization = async () => {
		if (!props.organizationUserId) return;

		try {
			const res = await organizationApi.getOrganizationUser(props.organizationUserId, {
				include: "applications, permissions",
			});
			setOrganizationUser(res);
			reset(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchApplications = async () => {
		if (organization.id === null) return;

		try {
			const res = await organizationApi.getOrganizationApplications({ include: "applications.roles" });
			setApplications(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const isLoadingData = () => {
		return props.organizationUserId && !organizationUser;
	};

	const getPermissions = (permissions?: string[]) => {
		return permissions?.map((per) => {
			return { value: per, label: per };
		});
	};

	return (
		<Modal show onHide={props.handleClose}>
			<Form noValidate onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t("modules.user.field.user.title")} "{organizationUser?.email}"
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{!isLoadingData() ? (
						<>
							{!props.organizationUserId && (
								<FormInput
									placeholder={t("modules.user.field.email.placeholder")}
									label={t("lib:common.word.email")}
									register={register}
									name="email"
									errors={errors}
								/>
							)}
							<FormSelectGroup
								label={t("modules.user.field.permissions.title")}
								isMulti
								name="permissions"
								errors={errors}
								defaultValue={getPermissions(organizationUser?.permissions)}
								options={permissions}
								control={control}
								data-testid="permissions"
							/>
							<OrganizationApplicationsTableForm
								form={form}
								applications={applications}
								fields={fields}
								remove={remove}
								append={append}
							/>
						</>
					) : (
						<>
							<Loading />
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					{!isLoadingData() && (
						<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
							{t("common.action.save", { ns: "lib" })}
						</ButtonLoading>
					)}
					<Button variant="light" onClick={props.handleClose}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};
export default OrganizationUserForm;
